import {
  type WorkplaceCommentsParams,
  type WorkplaceCommentType,
} from "@src/appV2/redesign/Workplace/Review/api/useWorkplaceComments";
import { useQueryClient } from "@tanstack/react-query";

import {
  deletedCommentListCache,
  deletePaginatedCommentListCache,
  deleteSingleCommentCache,
  updateCommentListCache,
  updatePaginatedCommentListCache,
  updateSingleCommentCache,
} from "./queryCache";

interface UseUpdateCachedCommentProps {
  workplaceId: string;
  commentsParams: WorkplaceCommentsParams;
}

/**
 * Returns commentsParams without searchText in the filter, so that cache updates
 * during a search also propagate to the base (non-search) cache.
 */
function getBaseCommentsParams(
  commentsParams: WorkplaceCommentsParams
): WorkplaceCommentsParams | undefined {
  if (!commentsParams.filter?.searchText) {
    return undefined;
  }

  const { searchText: _, ...restFilter } = commentsParams.filter;
  const filter = Object.keys(restFilter).length > 0 ? restFilter : undefined;
  return { ...commentsParams, filter };
}

export function useUpdateCachedComment(props: UseUpdateCachedCommentProps) {
  const { workplaceId, commentsParams } = props;
  const baseCommentsParams = getBaseCommentsParams(commentsParams);

  const queryClient = useQueryClient();

  const deleteComment = (commentId: string) => {
    const cacheParams = { queryClient, commentId, workplaceId: String(workplaceId) };

    deletedCommentListCache({ ...cacheParams, commentsParams });
    deletePaginatedCommentListCache({ ...cacheParams, commentsParams });

    if (baseCommentsParams) {
      deletedCommentListCache({ ...cacheParams, commentsParams: baseCommentsParams });
      deletePaginatedCommentListCache({ ...cacheParams, commentsParams: baseCommentsParams });
    }

    deleteSingleCommentCache({ ...cacheParams, commentsParams });
  };

  const updateComment = (comment: WorkplaceCommentType) => {
    const cacheParams = { queryClient, comment, workplaceId: String(workplaceId) };

    updateCommentListCache({ ...cacheParams, commentsParams });
    updatePaginatedCommentListCache({ ...cacheParams, commentsParams });

    if (baseCommentsParams) {
      updateCommentListCache({ ...cacheParams, commentsParams: baseCommentsParams });
      updatePaginatedCommentListCache({ ...cacheParams, commentsParams: baseCommentsParams });
    }

    updateSingleCommentCache({ ...cacheParams, commentsParams });
  };

  return { updateComment, deleteComment };
}
